import { z } from 'zod';

/**
 * Vertical Media Schemas
 *
 * Single source of truth for landing-page media references across all
 * verticals. See docs/architecture/vertical-media.md for the full rules
 * and .claude/clauses/critical-rules-vertical-media.md for the enforceable
 * short form.
 *
 * Three guarantees:
 * 1. Every video path MUST live under /videos/landing/<vertical>/...
 * 2. Every video MUST have a poster image (used for LCP and
 *    prefers-reduced-motion fallback).
 * 3. Every image MUST have an alt string (empty strings are accepted for
 *    decorative hero imagery; the field is required so copy is never
 *    forgotten).
 */

const IMAGE_EXT = /\.(jpe?g|png|webp|avif)$/i;
const VIDEO_EXT = /\.(mp4|webm|mov)$/i;

const videoPublicPath = z
  .string()
  .regex(/^\/videos\/landing\//, {
    message: 'Video URLs must live under /videos/landing/<vertical>/...',
  })
  .regex(VIDEO_EXT, {
    message: 'Video URLs must end with .mp4, .webm, or .mov',
  });

const imagePublicPath = z
  .string()
  .regex(/^\/images\/landing\//, {
    message: 'Image URLs must live under /images/landing/<vertical>/...',
  })
  .regex(IMAGE_EXT, {
    message: 'Image URLs must end with .jpg, .jpeg, .png, .webp, or .avif',
  });

const posterPublicPath = z.string().min(1).regex(IMAGE_EXT, {
  message: 'Poster URLs must be an image (.jpg, .jpeg, .png, .webp, or .avif)',
});

export const VideoAssetSchema = z.object({
  mp4: videoPublicPath,
  webm: videoPublicPath.optional(),
  poster: posterPublicPath,
});
export type VideoAsset = z.infer<typeof VideoAssetSchema>;

export const ImageAssetSchema = z.object({
  src: imagePublicPath,
  mobile: imagePublicPath.optional(),
  alt: z.string(),
});
export type ImageAsset = z.infer<typeof ImageAssetSchema>;

/**
 * A media slot can be image-only, or image + video. When both are present
 * the video is preferred on capable devices; the image is always the
 * reduced-motion and pre-roll fallback.
 */
export const MediaSlotSchema = z.object({
  image: ImageAssetSchema,
  video: VideoAssetSchema.optional(),
});
export type MediaSlot = z.infer<typeof MediaSlotSchema>;

/**
 * Media for a single testimonial. Kept separate from TrustSignal
 * (content, attribution, type) so media can be swapped without editing
 * the copy.
 */
export const TestimonialMediaSchema = z.object({
  portrait: imagePublicPath,
  video: videoPublicPath.optional(),
  altName: z.string().min(1),
});
export type TestimonialMedia = z.infer<typeof TestimonialMediaSchema>;

/**
 * Full media contract for a vertical (or a side/locale variant).
 *
 * - hero.default: required; shown for any city without a specific override
 * - hero.cities[slug]: optional per-city override
 * - sections[key]: keyed by semantic slot name (e.g. 'earnings-counter',
 *   'city-reveal', 'connection'). Layout code looks up by key.
 * - testimonials[key]: keyed to match TrustSignal.key so copy + media
 *   stay in sync.
 */
export const VerticalMediaSchema = z.object({
  hero: z.object({
    default: MediaSlotSchema,
    cities: z.record(z.string(), MediaSlotSchema).default({}),
  }),
  sections: z.record(z.string(), MediaSlotSchema).default({}),
  testimonials: z.record(z.string(), TestimonialMediaSchema).default({}),
});
export type VerticalMedia = z.infer<typeof VerticalMediaSchema>;
